import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { PricedFlight } from "@/types/landing";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { UseFormReturn } from "react-hook-form";
import { z } from "zod";
import { formatMoney } from "./PricedFlightDetail";

// --- Form Schema with Dates ---
export const exceptionFormSchema = z
  .object({
    name: z.string().min(1, "Exception name is required."),
    description: z.string().refine((val) => val.trim() !== "", {
      message: "Description is required.",
    }),
    status: z.enum(["ACTIVE", "DISABLED"]).default("ACTIVE"),
    signatory_status: z
      .enum(["BOTH", "SIGNATORY", "NON_SIGNATORY"])
      .default("BOTH"),
    fixed_fee_cents: z
      .string()
      .optional()
      .refine(
        (val) =>
          val === undefined || val.trim() === "" || !isNaN(parseFloat(val)),
        {
          message: "Must be a valid number or empty (for free)",
        }
      )
      .refine(
        (val) => val === undefined || val.trim() === "" || parseFloat(val) >= 0,
        {
          message: "Fee cannot be negative.",
        }
      ),
    criteria_type: z.enum(["airline", "weight", "tail_number"]),
    airline_code_iata: z.string().optional(),
    tail_number: z.string().optional(),
    min_weight_lbs: z
      .string()
      .optional()
      .refine(
        (val) => val === undefined || val.trim() === "" || /^[0-9]+$/.test(val),
        {
          message: "Must be a whole number or empty.",
        }
      ),
    max_weight_lbs: z
      .string()
      .optional()
      .refine(
        (val) => val === undefined || val.trim() === "" || /^[0-9]+$/.test(val),
        {
          message: "Must be a whole number or empty.",
        }
      ),
    effective_from: z.date().optional().nullable(),
    effective_until: z.date().optional().nullable(),
  })
  .refine(
    (data) => {
      if (data.criteria_type === "airline") {
        return !!data.airline_code_iata && data.airline_code_iata.trim() !== "";
      }
      return true;
    },
    { message: "Airline IATA code is required.", path: ["airline_code_iata"] }
  )
  .refine(
    (data) => {
      if (data.criteria_type === "weight") {
        return (
          (!!data.min_weight_lbs && data.min_weight_lbs.trim() !== "") ||
          (!!data.max_weight_lbs && data.max_weight_lbs.trim() !== "")
        );
      }
      return true;
    },
    {
      message: "At least Min or Max Weight is required.",
      path: ["min_weight_lbs"],
    }
  )
  .refine(
    (data) => {
      if (
        data.criteria_type === "weight" &&
        data.min_weight_lbs &&
        data.min_weight_lbs.trim() !== "" &&
        data.max_weight_lbs &&
        data.max_weight_lbs.trim() !== ""
      ) {
        return parseInt(data.min_weight_lbs) <= parseInt(data.max_weight_lbs);
      }
      return true;
    },
    {
      message: "Min Weight cannot be greater than Max Weight.",
      path: ["max_weight_lbs"],
    }
  )
  .refine(
    (data) => {
      if (data.effective_from && data.effective_until) {
        return data.effective_from <= data.effective_until;
      }
      return true;
    },
    {
      message: "Effective From date cannot be after Effective Until date.",
      path: ["effective_until"],
    }
  )
  .refine(
    (data) => {
      if (data.criteria_type === "tail_number") {
        return !!data.tail_number && data.tail_number.trim() !== "";
      }
      return true;
    },
    { message: "Tail number is required.", path: ["tail_number"] }
  );

export type ExceptionFormValues = z.infer<typeof exceptionFormSchema>;

// --- Helper Functions ---

// Converts cents back to dollar string ONLY FOR PRE-FILLING form (UpdateDialog)
export const centsToDollarString = (
  cents: number | null | undefined
): string => {
  if (cents === null || cents === undefined) return "";
  return (cents / 100).toFixed(2);
};

// Converts number to string ONLY FOR PRE-FILLING form (UpdateDialog)
export const numToString = (num: number | null | undefined): string => {
  if (num === null || num === undefined) return "";
  return num.toString();
};

// --- Component Props ---
interface LandingExceptionFormProps {
  form: UseFormReturn<ExceptionFormValues>;
  onSubmit: (values: ExceptionFormValues) => void;
  isLoading: boolean;
  submitButtonText: string;
  flight?: PricedFlight;
}

// --- Reusable Form Component ---
export function LandingExceptionForm({
  form,
  onSubmit,
  isLoading,
  submitButtonText,
  flight,
}: LandingExceptionFormProps) {
  const watchedCriteriaType = form.watch("criteria_type");

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {/* Two-column layout for form fields */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* First Column: Flight Info, Name, Description */}
          <div className="space-y-4">
            {/* Flight Information Display - Only show if flight data is provided */}
            {flight && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium">Flight Information</h3>
                <div className="text-sm text-gray-400 bg-slate-800 p-3 rounded">
                  <div>
                    <strong>Flight:</strong>{" "}
                    {flight.flight_number_display || "GA Flight"}
                  </div>
                  <div>
                    <strong>Route:</strong> {flight.departure_airport_code} →{" "}
                    {flight.arrival_airport_code}
                  </div>
                  <div>
                    <strong>Time:</strong> {flight.departure_time_display} -{" "}
                    {flight.arrival_time_display}
                  </div>
                  {!!flight.carrier && (
                    <div>
                      <strong>Carrier:</strong> {flight.carrier}
                    </div>
                  )}
                  {!!flight.tail_number && (
                    <div>
                      <strong>Tail Number:</strong> {flight.tail_number}
                    </div>
                  )}
                  {flight.landing_fee_cents !== null && (
                    <div>
                      <strong>Landing Fee:</strong>{" "}
                      {formatMoney(flight.landing_fee_cents)}
                    </div>
                  )}
                </div>
              </div>
            )}

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Exception Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g., Free Landings for AA"
                      {...field}
                      autoComplete="off"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Provide context for this exception"
                      {...field}
                      value={field.value ?? ""} // Handle null/undefined
                      autoComplete="off"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" disabled={isLoading} className="w-full">
              {isLoading ? "Saving..." : submitButtonText}
            </Button>
          </div>

          {/* Second Column: All other fields */}
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="fixed_fee_cents"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Fixed Fee (USD)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="Leave empty for Free ($0)"
                      {...field}
                      // Use the raw string value directly
                      value={field.value ?? ""}
                      onChange={field.onChange} // Pass event directly
                      autoComplete="off"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="effective_from"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Effective From (Optional)</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant={"outline"}
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !field.value && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {field.value ? (
                            format(field.value, "PPP") // Format date nicely
                          ) : (
                            <span>Pick a date</span>
                          )}
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value ?? undefined} // Pass selected date
                        onSelect={field.onChange} // Update form state on selection
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <FormDescription>
                    When this exception starts being active.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="effective_until"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Effective Until (Optional)</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant={"outline"}
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !field.value && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {field.value ? (
                            format(field.value, "PPP") // Format date nicely
                          ) : (
                            <span>Pick a date</span>
                          )}
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value ?? undefined} // Pass selected date
                        onSelect={field.onChange} // Update form state on selection
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <FormDescription>
                    When this exception stops being active.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="signatory_status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Applies To</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select signatory status" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="BOTH">
                        Both Signatory and Non-Signatory
                      </SelectItem>
                      <SelectItem value="SIGNATORY">Signatory Only</SelectItem>
                      <SelectItem value="NON_SIGNATORY">
                        Non-Signatory Only
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Whether this exception applies to signatory carriers,
                    non-signatory carriers, or both.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="criteria_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Criteria Type</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select criteria type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="airline">Airline</SelectItem>
                      <SelectItem value="weight">Weight</SelectItem>
                      <SelectItem value="tail_number">Tail Number</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {watchedCriteriaType === "airline" && (
              <FormField
                control={form.control}
                name="airline_code_iata"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Airline IATA Code</FormLabel>
                    <FormControl>
                      <Input
                        maxLength={3}
                        placeholder="e.g., AA"
                        {...field}
                        value={field.value ?? ""} // Handle null
                        autoComplete="off"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {watchedCriteriaType === "weight" && (
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="min_weight_lbs"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Min Weight (lbs)</FormLabel>
                      <FormControl>
                        <Input
                          type="number" // Still suggest number type to browser
                          placeholder="e.g., 0"
                          {...field}
                          // Use the raw string value directly
                          value={field.value ?? ""}
                          onChange={field.onChange} // Pass event directly
                          autoComplete="off"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="max_weight_lbs"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Max Weight (lbs)</FormLabel>
                      <FormControl>
                        <Input
                          type="number" // Still suggest number type to browser
                          placeholder="e.g., 10000"
                          {...field}
                          // Use the raw string value directly
                          value={field.value ?? ""}
                          onChange={field.onChange} // Pass event directly
                          autoComplete="off"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {watchedCriteriaType === "tail_number" && (
              <FormField
                control={form.control}
                name="tail_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tail Number</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g., N123AB"
                        {...field}
                        value={field.value ?? ""} // Handle null
                        autoComplete="off"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>
        </div>
      </form>
    </Form>
  );
}
